import "@progress/kendo-ui/js/kendo.numerictextbox";
import "@progress/kendo-ui/js/kendo.slider";
import "jquery-ui/ui/disable-selection";
import "jquery-ui/ui/widgets/sortable";
import * as ko from "knockout";
import "knockout-sortable";
import * as Constants from "../common/constants";
import * as EventUtil from "../common/eventUtil";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import { ICustomerDocument } from "../data/documentLibraryData";
import * as templateFormData from "../data/templateFormData";
import { ITemplateField } from "../data/templateFormData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import { ILanguage, ITranslatedText } from "../interfaces/translations";

interface IFormItemObservable {
    ID: KnockoutObservable<number>;
    TemplateFormID: KnockoutObservable<number>;
    ControlTypeID: KnockoutObservable<number>;
    FormText: KnockoutObservable<string>;
    FormTextTranslations: KnockoutObservableArray<ITranslatedTextObservable>;
    IsRequired: KnockoutObservable<boolean>;
    SortOrder: KnockoutObservable<number>;
    IsReadOnly: KnockoutObservable<boolean>;
    IsVisible: KnockoutObservable<boolean>;
    IsActive: KnockoutObservable<boolean>;
    MultipleAdditionalForms: KnockoutObservable<boolean>;
    CopyAnswerWhenCloningTask: KnockoutObservable<boolean>;
    ShowOnCalendar: KnockoutObservable<boolean>;
    IsFormTextMissing: KnockoutObservable<boolean>;
    IsQuestionTypeMissing: KnockoutObservable<boolean>;
    TemplateAdditionalFormID: KnockoutObservable<number>;
    TargetTemplateID: KnockoutObservable<number>;
    IsAdditionalFormMissing: KnockoutObservable<boolean>;
    MinMaxInvalid: KnockoutObservable<boolean>;
    ExternalName: KnockoutObservable<string>;
    ReportName: KnockoutObservable<string>;

    // for slider control
    ValidNumericDefaultValue: KnockoutObservable<boolean>;
    ValidInterval: KnockoutObservable<boolean>;

    DocumentFileTypeNotSelected: KnockoutObservable<boolean>;
    TemplatePhraseID: KnockoutObservable<number>;
    PhraseOrCustomChoice: KnockoutObservable<string>;
    PhraseOrCustomOptions: KnockoutObservableArray<any>;
    Fields: KnockoutObservableArray<IFieldObservable>;
    TemplateFormItemDocumentTypeAllowed: KnockoutObservableArray<ITemplateFormItemDocumentTypeAllowedObservable>;
    LinkedCustomerDocuments: KnockoutObservableArray<ITemplateFormItemLinkedCustomerDocumentObservable>;
    LinkedDocumentFilter: KnockoutObservable<string>;
    FilteredLinkedCustomerDocuments?: KnockoutComputed<ITemplateFormItemLinkedCustomerDocumentObservable[]>;
    IsFieldsMissing: KnockoutObservable<boolean>;
    TemplateControlTypeAttribute: ITemplateFormControlTypeAttribute;
    selected: KnockoutObservable<number>;
    newFieldText: KnockoutObservable<string>;
    IsEdit: KnockoutObservable<boolean>;
    HasError: KnockoutObservable<boolean>;
    templateName: KnockoutObservable<string>;
    removeClass?: (obj: any, parent: any) => void;
    addField?: (newfield: any) => void;
    removeField?: (dynamicField: any) => void;
    makeAllActive?: () => void;
    makeAllInActive?: () => void;
    formItemTitle: KnockoutComputed<string>;
    typeIsTextbox?: KnockoutComputed<boolean>;
    typeIsNumeric?: KnockoutComputed<boolean>;
    typeIsTextArea?: KnockoutComputed<boolean>;
    typeIsDate?: KnockoutComputed<boolean>;
    typeIsTime?: KnockoutComputed<boolean>;
    typeIsDateTime?: KnockoutComputed<boolean>;
    typeIsRadio?: KnockoutComputed<boolean>;
    typeIsDropdown?: KnockoutComputed<boolean>;
    typeIsCheckbox?: KnockoutComputed<boolean>;
    typeIsPhone?: KnockoutComputed<boolean>;
    typeIsSection?: KnockoutComputed<boolean>;
    typeIsButton?: KnockoutComputed<boolean>;
    typeIsTextDesc?: KnockoutComputed<boolean>;
    typeIsComments?: KnockoutComputed<boolean>;
    typeIsCommentsPics?: KnockoutComputed<boolean>;
    typeIsBarcode?: KnockoutComputed<boolean>;
    typeIsDocuments?: KnockoutComputed<boolean>;
    typeIsLinkedDocuments?: KnockoutComputed<boolean>;
    typeIsSignature?: KnockoutComputed<boolean>;
    typeIsTarget?: KnockoutComputed<boolean>;
    typeIsAddress?: KnockoutComputed<boolean>;
    typeIsGeoLocation?: KnockoutComputed<boolean>;
    emptyOrNullFormat?: KnockoutComputed<boolean>;
    typeIsSlider?: KnockoutComputed<boolean>;
    typeIsSingleCheckbox?: KnockoutComputed<boolean>;
}

interface ITemplateFormItemLinkedCustomerDocumentObservable {
    TemplateFormItemCustomerDocumentID: KnockoutObservable<number>;
    CustomerDocumentID: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    Description: KnockoutObservable<string>;
    FileName: KnockoutObservable<string>;
}

interface ITemplateFormItemDocumentTypeAllowedObservable {
    TemplateFormItemID: KnockoutObservable<number>;
    DocumentFileTypeAllowedID: KnockoutObservable<number>;
    DocumentTypeExtension: KnockoutObservable<string>;
    Description: KnockoutObservable<string>;
    IsActive: KnockoutObservable<boolean>;
}

interface ITemplateFormControlTypeAttribute {
    ID: KnockoutObservable<number>;
    MinVal: KnockoutObservable<string>;
    MaxVal: KnockoutObservable<string>;
    Format: KnockoutObservable<string>;
    MinLength: KnockoutObservable<string>;
    MaxLength: KnockoutObservable<string>;
    NumDecimals: KnockoutObservable<number>;
    NumericDefaultValue: KnockoutObservable<number>;
    Interval: KnockoutObservable<number>;
}

interface IFieldObservable {
    ID: KnockoutObservable<number>;
    FieldText: KnockoutObservable<string>;
    FieldValue: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    HasException: KnockoutObservable<boolean>;
    HasExceptionForm: KnockoutObservable<boolean>;
    TemplateFormID: KnockoutObservable<number | null>;
}

interface ITemplateFormObservable {
    ID: KnockoutObservable<number>;
    FormName: KnockoutObservable<string>;
    FormDescription: KnockoutObservable<string>;
    FormTypeID: KnockoutObservable<number>;
    IsActive: KnockoutObservable<boolean>;
    FormItems: KnockoutObservableArray<IFormItemObservable>;
    navTypeIsForm?: KnockoutComputed<boolean>;
    navTypeIsAdditionalForm?: KnockoutComputed<boolean>;
    navTypeIsAttribute?: KnockoutComputed<boolean>;
    spliceFormItem?: (index: any, form: any) => void;
    copyFormItem?: (index: any, form: any) => void;
    addFormItem?: (form: any) => void;
    removeFormItem?: (form: any) => void;
}

interface ITranslatedTextObservable {
    LanguageID: number;
    LanguageName: string;
    TranslatedText: KnockoutObservable<string>;
}

enum TabStateValues {
    Fields,
    Translations,
}

interface IViewModel {
    TemplateForm: ITemplateFormObservable;
    FormTypes: KnockoutObservableArray<any>;
    ControlTypes: KnockoutObservableArray<templateFormData.IControlType>;
    TargetTemplates: KnockoutObservableArray<any>;
    AdditionalForms: KnockoutObservableArray<any>;
    TemplatePhrases: KnockoutObservableArray<any>;
    DocumentFileTypesAllowed: templateFormData.IDocumentFileTypeAllowed[];
    CustomerDocuments: ICustomerDocument[];
    Errors?: KnockoutValidationErrors;
    QuestionLabel: KnockoutObservable<string>;
    QuestionsLabel: KnockoutObservable<string>;
    TabState: KnockoutObservable<TabStateValues>;
    TranslationLanguages: ILanguage[];
    TranslationLanguageIDSearch: KnockoutObservable<number>;
    TranslationFieldTextSearch: KnockoutObservable<string>;

    inactivateForm(user: any): void;
    activateForm(user: any): void;
    getFormatMasksForItem(formItem: IFormItemObservable): templateFormData.IFormatMask[];
    hasFormatMasksForItem(formItem: IFormItemObservable): boolean;
    isFieldTabActive(): boolean;
    isTranslationTabActive(): boolean;
    makeFieldTabActive(): void;
    makeTranslationTabActive(): void;
    getFilteredTranslatedText(formItemObservable: IFormItemObservable): ITranslatedTextObservable[];
}

export class TemplateFormController implements IController {

    private container: JQuery;
    private viewModel: IViewModel;
    private templateFormID: number;
    private fieldsOnLoad: any = {};
    private NavigationType = {
        Form: 1,
        AdditionalForm: 2,
    };

    constructor() {
        const me: TemplateFormController = this;
        const userSession = Global.app().userSession;
        const languages = (userSession && userSession.Languages) ? userSession.Languages : [];

        this.viewModel = {
            TemplateForm: {
                ID: ko.observable(0),
                FormName: ko.observable("").extend({ required: true }),
                FormDescription: ko.observable(""),
                FormTypeID: ko.observable(0).extend({ required: true }),
                IsActive: ko.observable(true),
                FormItems: ko.observableArray<IFormItemObservable>(),
            },
            QuestionLabel: ko.observable(Constants.LABEL_QUESTION),
            QuestionsLabel: ko.observable(Constants.LABEL_QUESTIONS),
            FormTypes: ko.observableArray(),
            ControlTypes: ko.observableArray(),
            AdditionalForms: ko.observableArray(),
            TemplatePhrases: ko.observableArray(),
            TargetTemplates: ko.observableArray(),
            DocumentFileTypesAllowed: [],
            CustomerDocuments: [],
            TabState: ko.observable(TabStateValues.Fields),
            TranslationLanguages: languages,
            TranslationLanguageIDSearch: ko.observable(),
            TranslationFieldTextSearch: ko.observable(""),

            inactivateForm(user: any): void {
                UI.showConfirmDialog("Are you sure you want to make this form inactive?",
                    () => { me.toggleFormStatus(); });
            },
            activateForm(user: any): void {
                UI.showConfirmDialog("Are you sure you want to make this form active?",
                    () => { me.toggleFormStatus(); });
            },
            getFormatMasksForItem(formItem: IFormItemObservable): templateFormData.IFormatMask[] {
                return me.getFormatMasksForControlType(formItem.ControlTypeID());
            },
            hasFormatMasksForItem(formItem: IFormItemObservable): boolean {
                return me.getFormatMasksForControlType(formItem.ControlTypeID()).length > 0;
            },
            isFieldTabActive(): boolean {
                return this.TabState() === TabStateValues.Fields;
           },
           isTranslationTabActive(): boolean {
               return this.TabState() === TabStateValues.Translations;
           },
           makeFieldTabActive(): void {
               this.TabState(TabStateValues.Fields);
           },
           makeTranslationTabActive(): void {
               this.TabState(TabStateValues.Translations);
           },
           getFilteredTranslatedText(formItemObservable: IFormItemObservable): ITranslatedTextObservable[] {
                const searchText = this.TranslationFieldTextSearch() ? this.TranslationFieldTextSearch().toLowerCase() : "";
                return formItemObservable.FormTextTranslations().filter((formTextTranslation) => {
                    return (
                                !this.TranslationLanguageIDSearch() ||
                                formTextTranslation.LanguageID === this.TranslationLanguageIDSearch()
                            )
                            &&
                            (
                                !this.TranslationFieldTextSearch() ||
                                (formItemObservable.FormText() && formItemObservable.FormText().toLowerCase().indexOf(searchText) >= 0));
                });
           },
        };

        this.viewModel.TemplateForm.navTypeIsForm = ko.pureComputed((): boolean => {
            return me.viewModel.TemplateForm.FormTypeID() === me.NavigationType.Form;
        });

        this.viewModel.TemplateForm.navTypeIsAdditionalForm = ko.pureComputed((): boolean => {
            return me.viewModel.TemplateForm.FormTypeID() === me.NavigationType.AdditionalForm;
        });

        this.viewModel.TemplateForm.spliceFormItem = (index: any, form: any): void => {
            for (const item of me.viewModel.TemplateForm.FormItems()) {
                item.IsEdit(false);
            }

            const formItem: templateFormData.ITemplateFormItem = {
                ID: 0,
                FormText: "",
                FormTextTranslations: [],
                TemplateFormID: me.viewModel.TemplateForm.FormItems()[index()].TemplateFormID(),
                ControlTypeID: 1,
                IsRequired: false,
                IsReadOnly: false,
                IsVisible: true,
                IsActive: true,
                IsEdit: true,
                TemplateAdditionalFormID: 0,
                Fields: [],
                TemplateFormItemDocumentTypeAllowed: [],
                LinkedCustomerDocuments: [],
                IsAdditionalFormMissing: false,
                MinMaxInvalid: false,
                ValidNumericDefaultValue: true,
                ValidInterval: true,
                selected: ko.observable(1),
                newFieldText: ko.observable(""),
                MultipleAdditionalForms: false,
                CopyAnswerWhenCloningTask: false,
                ShowOnCalendar: false,
                PhraseOrCustomChoice: Constants.TEMPLATE_FORM_CHOICES_CUSTOM,
                ExternalName: "",
                ReportName: "",

            };
            me.viewModel.TemplateForm.FormItems.splice(index() + 1, 0, me.getFormItemObservable(formItem));
        };

        this.viewModel.TemplateForm.copyFormItem = (index: any, form: any): void => {
            for (const item of me.viewModel.TemplateForm.FormItems()) {
                item.IsEdit(false);
            }

            const sourceFormItem = me.viewModel.TemplateForm.FormItems()[index()];
            const sourceFormItemDocs = me.getTemplateFormItemDocumentTypeAllowedFromObservable(sourceFormItem.TemplateFormItemDocumentTypeAllowed);
            sourceFormItemDocs.forEach( (i) => i.TemplateFormItemID = 0);
            const sourceFormItemLinkedDocs = me.getTemplateFormItemLinkedCustomerDocumentsFromObservable(sourceFormItem.LinkedCustomerDocuments);
            sourceFormItemLinkedDocs.forEach((doc) => doc.TemplateFormItemCustomerDocumentID = 0);
            const formItem: templateFormData.ITemplateFormItem = {
                ID: 0,
                FormText: sourceFormItem.FormText(),
                FormTextTranslations: me.createTranslationTextsFromObservables(sourceFormItem.FormTextTranslations),
                TemplateFormID: sourceFormItem.TemplateFormID(),
                ControlTypeID: sourceFormItem.ControlTypeID(),
                IsRequired: sourceFormItem.IsRequired(),
                IsReadOnly: sourceFormItem.IsReadOnly(),
                IsVisible: sourceFormItem.IsVisible(),
                IsActive: true,
                IsEdit: true,
                TemplateAdditionalFormID: sourceFormItem.TemplateAdditionalFormID(),
                TemplatePhraseID: sourceFormItem.TemplatePhraseID(),
                TargetTemplateID: sourceFormItem.TargetTemplateID(),
                IsAdditionalFormMissing: false,
                MinMaxInvalid: false,
                ValidNumericDefaultValue: true,
                ValidInterval: true,
                Fields: me.getDuplicateFields(sourceFormItem.Fields),
                TemplateFormItemDocumentTypeAllowed: sourceFormItemDocs,
                LinkedCustomerDocuments: sourceFormItemLinkedDocs,
                TemplateControlTypeAttribute: {
                    ID: 0,
                    MinVal: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.MinVal() : "",
                    MaxVal: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.MaxVal() : "",
                    Format: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.Format() : "",
                    MinLength: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.MinLength() : "0",
                    MaxLength: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.MaxLength() : "0",
                    NumDecimals: sourceFormItem.TemplateControlTypeAttribute ?
                        sourceFormItem.TemplateControlTypeAttribute.NumDecimals() : 0,
                    NumericDefaultValue: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.NumericDefaultValue() : 0,
                    Interval: sourceFormItem.TemplateControlTypeAttribute ? sourceFormItem.TemplateControlTypeAttribute.Interval() : 0,
                },
                selected: ko.observable(1),
                newFieldText: ko.observable(""),
                MultipleAdditionalForms: sourceFormItem.MultipleAdditionalForms(),
                CopyAnswerWhenCloningTask: sourceFormItem.CopyAnswerWhenCloningTask(),
                ShowOnCalendar: sourceFormItem.ShowOnCalendar(),
                PhraseOrCustomChoice: sourceFormItem.PhraseOrCustomChoice(),
                ExternalName: sourceFormItem.ExternalName(),
                ReportName: sourceFormItem.ReportName(),
            };

            me.viewModel.TemplateForm.FormItems.splice(index() + 1, 0, me.getFormItemObservable(formItem));
        };

        this.viewModel.TemplateForm.addFormItem = (form: any) => {
            for (const item of me.viewModel.TemplateForm.FormItems()) {
                if (item.ID() !== form.TemplateForm.ID()) {
                    item.IsEdit(false);
                }
            }

            const formItem: templateFormData.ITemplateFormItem = {
                ID: 0,
                FormText: "",
                FormTextTranslations: [],
                TemplateFormID: form.TemplateForm.ID(),
                ControlTypeID: 1,
                IsRequired: false,
                IsReadOnly: false,
                IsVisible: true,
                IsActive: true,
                IsEdit: true,
                TemplateAdditionalFormID: 0,
                Fields: [],
                TemplateFormItemDocumentTypeAllowed: [],
                LinkedCustomerDocuments: [],
                IsAdditionalFormMissing: false,
                MinMaxInvalid: false,
                ValidNumericDefaultValue: true,
                ValidInterval: true,
                selected: ko.observable(1),
                newFieldText: ko.observable(""),
                MultipleAdditionalForms: false,
                CopyAnswerWhenCloningTask: false,
                ShowOnCalendar: false,
                PhraseOrCustomChoice: Constants.TEMPLATE_FORM_CHOICES_CUSTOM,
                ExternalName: "",
                ReportName: "",
            };
            me.viewModel.TemplateForm.FormItems.push(me.getFormItemObservable(formItem));
        };

        this.viewModel.TemplateForm.removeFormItem = (form: any) => {
            UI.showConfirmDialog("Are you sure you want to delete \"" + form.formItemTitle() + "\"?",
                () => { me.DeleteFormItem(form); });
        };

        this.viewModel.Errors = ko.validation.group(this.viewModel.TemplateForm, { deep: true });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        this.container.find(".sortable").sortable();
        this.container.find(".sortable").disableSelection();

        this.container.find(".sortable2").sortable();
        this.container.find(".sortable2").disableSelection();

        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {

        if (params != null && params.length === 1) {
            this.templateFormID = params[0];
        } else {
            this.templateFormID = 0;
        }

        return this.loadRecord(this.templateFormID);
    }

    public save(): JQueryPromise<any> {
        const me: TemplateFormController = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (!this.isValid()) {
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            deferred.reject();
            return deferred.promise();
        }

        waitContainer = UI.showWaitIndicator(me.container);
        templateFormData.SaveTemplateForm(this.getTemplateForm())
            .done((response: Data.IResponse<templateFormData.ITemplateFormResponse>) => {
                if (response.Success) {
                    if (me.viewModel.TemplateForm.ID() <= 0) {
                        // add a new record, so update the URL
                        me.updateURL(response.Payload.ID, me.viewModel.TemplateForm.FormName());
                    } else {
                        // updating an existing record
                        app.pageManager().refreshCurrentPageTitle();
                    }
                    me.loadRecord(response.Payload.ID);
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.getTemplateForm());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "TemplateForm";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    // returns a title for the page
    public title(): string {
        return "Form Template";
    }

    public subTitle(): string {
        let subTitle: string = "";
        if (this.templateFormID == null) {
            subTitle = "New";
        } else {
            subTitle = this.viewModel.TemplateForm.FormName();
        }
        return subTitle;
    }

    private GetTemplateName(controlType: number): string {
        if (controlType === Constants.QuestionType.TEXTBOX) {
            return "templateform_text-template";
        } else if (controlType === Constants.QuestionType.DROPDOWN) {
            return "templateform_select-template";
        } else if (controlType === Constants.QuestionType.RADIOBUTTONLIST) {
            return "templateform_radio-template";
        } else if (controlType === Constants.QuestionType.CHECKBOXLIST) {
            return "templateform_checkbox-template";
        } else if (controlType === Constants.QuestionType.DATE) {
            return "templateform_date-template";
        } else if (controlType === Constants.QuestionType.TEXTAREA) {
            return "templateform_textarea-template";
        } else if (controlType === Constants.QuestionType.NUMERIC) {
            return "templateform_numeric-template";
        } else if (controlType === Constants.QuestionType.SECTION) {
            return "templateform_section-template";
        } else if (controlType === Constants.QuestionType.TEXTDESCRIPTION) {
            return "templateform_textdescription-template";
        } else if (controlType === Constants.QuestionType.BUTTON) {
            return "templateform_button-template";
        } else if (controlType === Constants.QuestionType.SIGNATURE) {
            return "templateform_signature-template";
        } else if (controlType === Constants.QuestionType.MULTIPLECOMMENTS) {
            return "templateform_multiplecomments-template";
        } else if (controlType === Constants.QuestionType.MULTIPLECOMMENTSPICS) {
            return "templateform_multiplecommentspics-template";
        } else if (controlType === Constants.QuestionType.BARCODE) {
            return "templateform_barcode-template";
        } else if (controlType === Constants.QuestionType.DOCUMENTS) {
            return "templateform_documents-template";
        } else if (controlType === Constants.QuestionType.DATETIME) {
            return "templateform_datetime-template";
        } else if (controlType === Constants.QuestionType.TIME) {
            return "templateform_time-template";
        } else if (controlType === Constants.QuestionType.EMAIL) {
            return "templateform_email-template";
        } else if (controlType === Constants.QuestionType.PHONE) {
            return "templateform_phone-template";
        } else if (controlType === Constants.QuestionType.LINKEDDOCUMENTS) {
            return "templateform_linkeddocuments-template";
        } else if (controlType === Constants.QuestionType.TARGET) {
            return "templateform_target-template";
        } else if (controlType === Constants.QuestionType.ADDRESS) {
            return "templateform_address-template";
        } else if (controlType === Constants.QuestionType.GEOLOCAITON) {
            return "templateform_geolocation-template";
        } else if (controlType === Constants.QuestionType.SLIDER) {
            return "templateform_slider-template";
        } else if (controlType === Constants.QuestionType.SINGLECHECKBOX) {
            return "templateform_singlecheckbox-template";
        } else {
            return "";
        }
    }

    private DeleteFormItem(form: any): void {
        const me: TemplateFormController = this;

        if (form.ID() === 0) {
            me.viewModel.TemplateForm.FormItems.remove(form);
        } else {
            form.IsActive(false);
        }
    }

    private loadRecord(templateFormID: number): JQueryPromise<any> {
        const me: TemplateFormController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        const templateFormRequest: any = {
            TemplateFormID: templateFormID,
        };
        templateFormData.GetTemplateForm(templateFormRequest)
            .done((response: Data.IResponse<templateFormData.ITemplateFormMaster>) => {

                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private getTemplateFormItemDocumentTypeAllowedFromDocumentFileTypeAllowed(documentFileTypesAllowed: templateFormData.IDocumentFileTypeAllowed[])
        : templateFormData.ITemplateFormItemDocumentTypeAllowed[] {
        const templateFormItemDocumentTypesAllowed: templateFormData.ITemplateFormItemDocumentTypeAllowed[] = [];

        documentFileTypesAllowed.forEach((documentFileTypeAllowed: templateFormData.IDocumentFileTypeAllowed) => {
            const templateFormItemDocumentTypeAllowed: templateFormData.ITemplateFormItemDocumentTypeAllowed = {
                TemplateFormItemID: 0,
                DocumentFileTypeAllowedID: documentFileTypeAllowed.ID,
                DocumentTypeExtension: documentFileTypeAllowed.DocumentTypeExtension,
                Description: documentFileTypeAllowed.Description,
                IsActive: true,
            };
            templateFormItemDocumentTypesAllowed.push(templateFormItemDocumentTypeAllowed);
        });
        return templateFormItemDocumentTypesAllowed;
    }

    private createTemplateFormItemDocumentTypeAllowedObservable(templateFormItemDocumentTypeAllowed: templateFormData.ITemplateFormItemDocumentTypeAllowed)
        : ITemplateFormItemDocumentTypeAllowedObservable {
        const vmtemplateFormItemDocumentTypeAllowed: ITemplateFormItemDocumentTypeAllowedObservable = {
            TemplateFormItemID: ko.observable(templateFormItemDocumentTypeAllowed.TemplateFormItemID),
            DocumentFileTypeAllowedID: ko.observable(templateFormItemDocumentTypeAllowed.DocumentFileTypeAllowedID),
            Description: ko.observable(templateFormItemDocumentTypeAllowed.Description),
            IsActive: ko.observable(templateFormItemDocumentTypeAllowed.IsActive),
            DocumentTypeExtension: ko.observable(templateFormItemDocumentTypeAllowed.DocumentTypeExtension),
        };

        return vmtemplateFormItemDocumentTypeAllowed;
    }

    private getTemplateFormItemDocumentTypeAllowedFromObservable(docTypesAllowed: KnockoutObservableArray<ITemplateFormItemDocumentTypeAllowedObservable>)
        : templateFormData.ITemplateFormItemDocumentTypeAllowed[] {
        const templateFormDocumentTypesAllowed: templateFormData.ITemplateFormItemDocumentTypeAllowed[] = [];

        docTypesAllowed().forEach((formItemDocumentTypeAllowed: ITemplateFormItemDocumentTypeAllowedObservable) => {
            const templateFormDocumentTypeAllowed: templateFormData.ITemplateFormItemDocumentTypeAllowed = {
                TemplateFormItemID: formItemDocumentTypeAllowed.TemplateFormItemID(),
                DocumentFileTypeAllowedID: formItemDocumentTypeAllowed.DocumentFileTypeAllowedID(),
                Description: formItemDocumentTypeAllowed.Description(),
                IsActive: formItemDocumentTypeAllowed.IsActive(),
                DocumentTypeExtension: formItemDocumentTypeAllowed.DocumentTypeExtension(),
            };
            templateFormDocumentTypesAllowed.push(templateFormDocumentTypeAllowed);
        });
        return templateFormDocumentTypesAllowed;
    }

    private updateViewModel(master: templateFormData.ITemplateFormMaster): void {
        const me: TemplateFormController = this;

        me.viewModel.ControlTypes(master.ControlTypes);
        me.viewModel.TemplatePhrases([]);
        me.viewModel.FormTypes([]);
        me.viewModel.AdditionalForms([]);
        me.viewModel.TemplateForm.FormItems([]);
        me.viewModel.DocumentFileTypesAllowed = [];

        me.viewModel.DocumentFileTypesAllowed = master.DocumentFileTypes;
        me.viewModel.CustomerDocuments = master.CustomerDocuments;

        master.TargetTemplates.forEach((targetTemplate: Data.ICodeModel<number>) => {
            me.viewModel.TargetTemplates.push({ ID: targetTemplate.ID, Name: targetTemplate.Name });
        });

        me.viewModel.TemplatePhrases.push(me.GetCodeModelObservable({ ID: null, Name: Constants.PHRASE_NOTSELECTED }));
        master.TemplatePhrases.forEach((templatePhrases: Data.ICodeModel<number>) => {
            me.viewModel.TemplatePhrases.push(me.GetCodeModelObservable(templatePhrases));
        });

        master.FormTypes.forEach((formTypes: Data.ICodeModel<number>) => {
            me.viewModel.FormTypes.push(me.GetCodeModelObservable(formTypes));
        });

        me.viewModel.AdditionalForms.push(me.GetCodeModelObservable({ ID: null, Name: Constants.PHRASE_NOTSELECTED }));
        master.AdditionalForms.forEach((additionalForms: Data.ICodeModel<number>) => {
            me.viewModel.AdditionalForms.push(me.GetCodeModelObservable(additionalForms));
        });

        me.viewModel.TemplateForm.ID(master.ID);
        me.viewModel.TemplateForm.FormName(master.FormName);
        me.viewModel.TemplateForm.FormDescription(master.FormDescription);
        me.viewModel.TemplateForm.IsActive(master.IsActive);
        me.viewModel.TemplateForm.FormTypeID(master.FormTypeID);

        if (master.FormItems) {
            master.FormItems.forEach((formItem: templateFormData.ITemplateFormItem) => {
                me.viewModel.TemplateForm.FormItems.push(me.getFormItemObservable(formItem));
            });
        }

        this.fieldsOnLoad = ko.toJSON(this.getTemplateForm());
    }

    private toggleFormStatus(): void {
        const me: TemplateFormController = this;
        const newIsActive: any = !this.viewModel.TemplateForm.IsActive();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);
        const formStatusChanged: boolean = me.haveFieldsChanged();

        templateFormData.ChangeFormStatus(this.viewModel.TemplateForm.ID(), newIsActive)
            .done((response: Data.IResponse<any>) => {
                if (response.Success) {
                    me.viewModel.TemplateForm.IsActive(newIsActive);
                    if (!formStatusChanged) {
                        me.fieldsOnLoad = ko.toJSON(me.getTemplateForm());
                    }
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });
    }

    private getFormItemObservable(formItem: templateFormData.ITemplateFormItem): IFormItemObservable {
        const me: TemplateFormController = this;

        const getFormTextTranslationObservables = (formTextTranslations: ITranslatedText[]): ITranslatedTextObservable[] => {
            const formTextTranslationObservables = me.createEmptyFormTextTranslations();
            if (formTextTranslations) {
                for (const formTextTranslationObservable of formTextTranslationObservables) {
                    const matchingFormTextTranslation = formTextTranslations.filter((formTextTranslation) =>
                                                    formTextTranslation.LanguageID === formTextTranslationObservable.LanguageID);
                    if (matchingFormTextTranslation.length > 0) {
                        formTextTranslationObservable.TranslatedText(matchingFormTextTranslation[0].TranslatedText);
                    }
                }
            }
            return formTextTranslationObservables;
        };

        const formItemObservable: IFormItemObservable = {
            ID: ko.observable(formItem ? formItem.ID : 0),
            TemplateFormID: ko.observable(formItem ? formItem.TemplateFormID : 0),
            ControlTypeID: ko.observable(formItem ? formItem.ControlTypeID : 0).extend({ required: true }),
            formItemTitle: ko.pureComputed((): string => {
                return formItemObservable.FormText() ? formItemObservable.FormText() : "New Item";
            }),
            FormText: ko.observable(formItem ? formItem.FormText : "").extend({ required: true }),
            FormTextTranslations: ko.observableArray(getFormTextTranslationObservables(formItem.FormTextTranslations)),
            IsRequired: ko.observable(formItem ? formItem.IsRequired : false),
            SortOrder: ko.observable(formItem ? formItem.SortOrder : 0),
            IsReadOnly: ko.observable(formItem ? formItem.IsReadOnly : false),
            IsVisible: ko.observable(formItem ? formItem.IsVisible : true),
            IsActive: ko.observable(formItem ? formItem.IsActive : true),
            IsFormTextMissing: ko.observable(false),
            IsQuestionTypeMissing: ko.observable<boolean>(false),
            TemplateAdditionalFormID: ko.observable(formItem ? formItem.TemplateAdditionalFormID : 0),
            TargetTemplateID: ko.observable(formItem ? formItem.TargetTemplateID : null),
            IsAdditionalFormMissing: ko.observable(false),
            MinMaxInvalid: ko.observable(false),
            ValidNumericDefaultValue: ko.observable(true),
            ValidInterval: ko.observable(true),
            DocumentFileTypeNotSelected: ko.observable(false),
            TemplatePhraseID: ko.observable(formItem ? formItem.TemplatePhraseID : 0),
            PhraseOrCustomChoice: ko.observable(formItem.TemplatePhraseID == null ? Constants.TEMPLATE_FORM_CHOICES_CUSTOM : Constants.TEMPLATE_FORM_CHOICES_PHRASES),
            PhraseOrCustomOptions: ko.observableArray([{ text: "Enter Values", value: Constants.TEMPLATE_FORM_CHOICES_CUSTOM },
                    { text: "Use Template Phrases", value: Constants.TEMPLATE_FORM_CHOICES_PHRASES}]),
            Fields: ko.observableArray<IFieldObservable>(),
            TemplateFormItemDocumentTypeAllowed: ko.observableArray<ITemplateFormItemDocumentTypeAllowedObservable>(),
            LinkedCustomerDocuments: ko.observableArray<ITemplateFormItemLinkedCustomerDocumentObservable>(),
            LinkedDocumentFilter: ko.observable(),
            ExternalName: ko.observable(formItem ? formItem.ExternalName : ""),
            ReportName: ko.observable(formItem ? formItem.ReportName : ""),

            IsFieldsMissing: ko.observable<boolean>(false),
            TemplateControlTypeAttribute: {
                ID: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.ID : 0),
                MinVal: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.MinVal : ""),
                MaxVal: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.MaxVal : ""),
                Format: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.Format : null),
                MinLength: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.MinLength : ""),
                MaxLength: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.MaxLength : ""),
                NumDecimals: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.NumDecimals : 0),
                NumericDefaultValue: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.NumericDefaultValue : 0),
                Interval: ko.observable(formItem.TemplateControlTypeAttribute ? formItem.TemplateControlTypeAttribute.Interval : 0),
            },
            selected: ko.observable(1),
            newFieldText: ko.observable(""),
            IsEdit: ko.observable(formItem ? formItem.IsEdit : false),
            HasError: ko.observable(false),
            templateName: ko.pureComputed<string>(() => {
                return me.GetTemplateName(formItemObservable.ControlTypeID());
            }),
            MultipleAdditionalForms: ko.observable(formItem.MultipleAdditionalForms),
            CopyAnswerWhenCloningTask: ko.observable(formItem.CopyAnswerWhenCloningTask),
            ShowOnCalendar: ko.observable(formItem.ShowOnCalendar),
        };

        formItemObservable.TargetTemplateID.extend({
            required: {
                onlyIf: () => {
                    return formItemObservable.typeIsTarget && formItemObservable.typeIsTarget();
                },
                message: "Target Template is required",
            },
        });

        formItemObservable.TemplateControlTypeAttribute.MinVal.extend({
            required: {
                onlyIf: () => { return (formItemObservable.ControlTypeID() === Constants.QuestionType.SLIDER);
                },
                message: "Minimum value is required.",
            },
        });

        formItemObservable.TemplateControlTypeAttribute.MaxVal.extend({
            required: {
                onlyIf: () => { return (formItemObservable.ControlTypeID() === Constants.QuestionType.SLIDER);
                },
                message: "Maximum value is required.",
            },
        });

        formItemObservable.FilteredLinkedCustomerDocuments = ko.pureComputed(() => {
            const filter = formItemObservable.LinkedDocumentFilter();
            return formItemObservable.LinkedCustomerDocuments().filter((item) => {
                if (!filter) { return true; }
                return item.FileName().indexOf(filter) >= 0;
            });
        });

        formItemObservable.removeClass = (obj: any, parent: any) => {
            let i: number;
            for (i = 0; i < parent.TemplateForm.FormItems().length; i++) {
                if (parent.TemplateForm.FormItems()[i].ID() !== obj.ID()) {
                    parent.TemplateForm.FormItems()[i].IsEdit(false);
                }
            }

            obj.IsEdit(!obj.IsEdit());
        };

        formItemObservable.addField = (newfield: any) => {
            const field: ITemplateField = {
                ID: 0,
                FieldText: newfield.newFieldText(),
                HasExceptionForm: false,
                IsActive: true,
                FieldValue: "",
                HasException: false,
                SortOrder: 0,
                TemplateFormID: null,
            };
            if (newfield.newFieldText() !== "") {
                formItemObservable.Fields.push(me.createIFieldObservable(field));
                newfield.newFieldText("");
                me.container.find("input[data-id='autoFocusOnCurrentField']").focus();
            }
        };

        formItemObservable.removeField = (field: IFieldObservable) => {
            if (field.ID() === 0) {
                formItemObservable.Fields.remove(field);
                me.container.find("input[data-id='autoFocusOnCurrentField']").focus();
            } else {
                field.IsActive(false);
            }
        };

        formItemObservable.makeAllActive = () => {
            formItemObservable.TemplateFormItemDocumentTypeAllowed().forEach(
                (templateFormItemDocumentTypeAllowed: ITemplateFormItemDocumentTypeAllowedObservable) => {
                    templateFormItemDocumentTypeAllowed.IsActive(true);
                });
        };

        formItemObservable.makeAllInActive = () => {
            formItemObservable.TemplateFormItemDocumentTypeAllowed().forEach(
                (templateFormItemDocumentTypeAllowed: ITemplateFormItemDocumentTypeAllowedObservable) => {
                    templateFormItemDocumentTypeAllowed.IsActive(false);
                });
        };

        for (const field of formItem.Fields) {
            formItemObservable.Fields.push(me.createIFieldObservable(field));
        }

        if (formItem.TemplateFormItemDocumentTypeAllowed.length === 0) {
            const allFileTypesAllowed = me.getTemplateFormItemDocumentTypeAllowedFromDocumentFileTypeAllowed(me.viewModel.DocumentFileTypesAllowed);
            allFileTypesAllowed.forEach((item) => {
                formItemObservable.TemplateFormItemDocumentTypeAllowed.push(me.createTemplateFormItemDocumentTypeAllowedObservable(item));
            });
        } else {
            formItem.TemplateFormItemDocumentTypeAllowed.forEach((item) => {
                formItemObservable.TemplateFormItemDocumentTypeAllowed.push(me.createTemplateFormItemDocumentTypeAllowedObservable(item));
            });
        }

        if (formItem.LinkedCustomerDocuments.length === 0) {
            me.viewModel.CustomerDocuments.forEach((item) => {
                formItemObservable.LinkedCustomerDocuments.push({
                    CustomerDocumentID: ko.observable(item.ID),
                    Description: ko.observable(item.Description),
                    FileName: ko.observable(item.FileName),
                    IsActive: ko.observable(false),
                    TemplateFormItemCustomerDocumentID: ko.observable(0),
                });
            });
        } else {
            formItem.LinkedCustomerDocuments.forEach((item) => {
                formItemObservable.LinkedCustomerDocuments.push({
                    CustomerDocumentID: ko.observable(item.CustomerDocumentID),
                    Description: ko.observable(item.Description),
                    FileName: ko.observable(item.FileName),
                    IsActive: ko.observable(item.IsActive),
                    TemplateFormItemCustomerDocumentID: ko.observable(item.TemplateFormItemCustomerDocumentID),
                });
            });
        }

        formItemObservable.typeIsTextbox = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.TEXTBOX;
        });

        formItemObservable.typeIsNumeric = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.NUMERIC;
        });

        formItemObservable.typeIsTextArea = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.TEXTAREA;
        });

        formItemObservable.typeIsDate = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.DATE;
        });

        formItemObservable.typeIsTime = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.TIME;
        });

        formItemObservable.typeIsDateTime = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.DATETIME;
        });

        formItemObservable.typeIsRadio = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.RADIOBUTTONLIST;
        });

        formItemObservable.typeIsDropdown = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.DROPDOWN;
        });

        formItemObservable.typeIsCheckbox = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.CHECKBOXLIST;
        });

        formItemObservable.typeIsSection = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.SECTION;
        });

        formItemObservable.typeIsButton = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.BUTTON;
        });

        formItemObservable.typeIsTextDesc = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.TEXTDESCRIPTION;
        });

        formItemObservable.typeIsComments = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.MULTIPLECOMMENTS;
        });

        formItemObservable.typeIsCommentsPics = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.MULTIPLECOMMENTSPICS;
        });

        formItemObservable.typeIsBarcode = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.BARCODE;
        });

        formItemObservable.typeIsDocuments = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.DOCUMENTS;
        });

        formItemObservable.typeIsLinkedDocuments = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.LINKEDDOCUMENTS;
        });

        formItemObservable.typeIsSignature = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.SIGNATURE;
        });

        formItemObservable.typeIsTarget = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.TARGET;
        });

        formItemObservable.typeIsAddress = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.ADDRESS;
        });

        formItemObservable.typeIsGeoLocation = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.GEOLOCAITON;
        });

        formItemObservable.typeIsSlider = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.SLIDER;
        });

        formItemObservable.typeIsSingleCheckbox = ko.pureComputed((): boolean => {
            return formItemObservable.ControlTypeID() === Constants.QuestionType.SINGLECHECKBOX;
        });

        formItemObservable.emptyOrNullFormat = ko.pureComputed((): boolean => {
            if (formItemObservable.TemplateControlTypeAttribute.Format()) {
                return false;
            } else {
                return true;
            }
        });
        formItemObservable.PhraseOrCustomChoice.subscribe((data: any) => {

            if (!data) { return; }
            // Clear out form fields but we don't have the template id yet
            if (data === Constants.TEMPLATE_FORM_CHOICES_CUSTOM) {
                // Clear the TemplatePhraseID
                formItemObservable.TemplatePhraseID(null);
            }

        });
        return formItemObservable;
    }

    private createIFieldObservable(field: ITemplateField): any {
        const vmField: IFieldObservable = {
            ID: ko.observable(field.ID),
            FieldText: ko.observable(field.FieldText),
            FieldValue: ko.observable(field.FieldValue),
            SortOrder: ko.observable(field.SortOrder),
            HasException: ko.observable(field.HasException),
            HasExceptionForm: ko.observable(field.HasExceptionForm),
            IsActive: ko.observable(field.IsActive),
            TemplateFormID: ko.observable(field.TemplateFormID),
        };
        return vmField;
    }

    private isValid(): boolean {
        const me: TemplateFormController = this;
        let i: number;
        let isValid: boolean = true;
        const templateForm: ITemplateFormObservable = this.viewModel.TemplateForm;
        const errors: KnockoutValidationErrors | undefined = this.viewModel.Errors;

        if (errors && errors() && errors().length > 0) {
            errors.showAllMessages(true);
            isValid = false;
        }

        templateForm.FormItems().forEach((formItem: IFormItemObservable) => {
            if (formItem.IsActive()) {
                let formItemIsValid: boolean = true;

                formItemIsValid = UI.setRequiredString(formItem.FormText, formItem.IsFormTextMissing) ? formItemIsValid : false;

                formItem.IsFieldsMissing(false);
                if (( formItem.TemplatePhraseID() === null) && me.numberOfActiveFields(formItem.Fields) === 0 &&
                    (formItem.ControlTypeID() === Constants.QuestionType.CHECKBOXLIST ||
                        formItem.ControlTypeID() === Constants.QuestionType.DROPDOWN ||
                        formItem.ControlTypeID() === Constants.QuestionType.RADIOBUTTONLIST)) {
                    formItem.IsFieldsMissing(true);
                    formItemIsValid = false;
                }
                if (formItem.ControlTypeID() === Constants.QuestionType.TEXTAREA ||
                    formItem.ControlTypeID() === Constants.QuestionType.TEXTBOX ||
                    formItem.ControlTypeID() === Constants.QuestionType.BARCODE) {
                    formItemIsValid = UI.validateMinMaxNumericValues(formItem.TemplateControlTypeAttribute.MinLength,
                        formItem.TemplateControlTypeAttribute.MaxLength, false, formItem.MinMaxInvalid) ? formItemIsValid : false;
                }
                if (formItem.ControlTypeID() === Constants.QuestionType.NUMERIC ||
                    formItem.ControlTypeID() === Constants.QuestionType.SLIDER) {
                    formItemIsValid = UI.validateMinMaxNumericValues(formItem.TemplateControlTypeAttribute.MinVal,
                        formItem.TemplateControlTypeAttribute.MaxVal, true, formItem.MinMaxInvalid) ? formItemIsValid : false;
                }
                if (( formItem.TemplatePhraseID() === null) && (formItem.ControlTypeID() === Constants.QuestionType.DROPDOWN ||
                    formItem.ControlTypeID() === Constants.QuestionType.RADIOBUTTONLIST ||
                    formItem.ControlTypeID() === Constants.QuestionType.CHECKBOXLIST)) {
                    for (i = 0; i < formItem.Fields().length; i++) {
                        // When Multiple Additionalform is checked, the HasExceptionForm(dropdown) is optional.
                        if (!formItem.MultipleAdditionalForms() && formItem.Fields()[i].HasExceptionForm()
                                && formItem.Fields()[i].IsActive()) {
                            formItemIsValid = UI.setRequiredNumber(formItem.TemplateAdditionalFormID,
                                formItem.IsAdditionalFormMissing) ? formItemIsValid : false;
                        }
                    }
                }

                if (formItem.ControlTypeID() === Constants.QuestionType.DOCUMENTS) {
                    formItem.DocumentFileTypeNotSelected(!formItem.TemplateFormItemDocumentTypeAllowed().some((x) => x.IsActive()));
                    formItemIsValid = formItem.DocumentFileTypeNotSelected() ? false : formItemIsValid;
                }

                if (formItem.ControlTypeID() === Constants.QuestionType.SLIDER) {
                    formItemIsValid = me.validateSliderDefaultValueAndInterval(formItem) ? formItemIsValid : false;
                }
                formItem.HasError(!formItemIsValid);

                if (!formItemIsValid) {
                    isValid = false;
                }
            }
        });

        return isValid;
    }
    private validateSliderDefaultValueAndInterval( formItem: IFormItemObservable): boolean {
        const me: TemplateFormController = this;
        formItem.ValidNumericDefaultValue(me.validDefaultValue(formItem));
        formItem.ValidInterval(formItem.TemplateControlTypeAttribute.Interval() > 0);

        return formItem.ValidInterval() && formItem.ValidNumericDefaultValue();
    }

    private validDefaultValue(formItem: IFormItemObservable ) {
        let isValid = false;
        const min: number = parseInt(formItem.TemplateControlTypeAttribute.MinVal(), 10);
        const max: number = parseInt(formItem.TemplateControlTypeAttribute.MaxVal(), 10);

        if (!isNaN(min) && !isNaN(max) &&
            (formItem.TemplateControlTypeAttribute.NumericDefaultValue() >= min &&
             formItem.TemplateControlTypeAttribute.NumericDefaultValue() <= max)) {
            isValid = true;
        }
        return isValid;
    }
    private numberOfActiveFields(fields: KnockoutObservableArray<IFieldObservable>): number {
        let count: number = 0;

        fields().forEach((field: IFieldObservable) => {
            if (field.IsActive()) {
                count++;
            }
        });
        return count;
    }

    // -------------------------------------------------------------------------
    // functions for getting the data structure needed
    // to update the template on the server
    // -------------------------------------------------------------------------

    private getTemplateForm(): templateFormData.ITemplateForm {
        const templateForm: templateFormData.ITemplateForm = {
            ID: this.viewModel.TemplateForm.ID(),
            FormName: this.viewModel.TemplateForm.FormName(),
            FormDescription: this.viewModel.TemplateForm.FormDescription(),
            FormTypeID: this.viewModel.TemplateForm.FormTypeID(),
            IsActive: this.viewModel.TemplateForm.IsActive(),
            FormItems: this.getTemplateFormItems(),
        };

        return templateForm;
    }

    private getControlTypeAttribute(controlTypeAttribute: ITemplateFormControlTypeAttribute):
        templateFormData.ITemplateControlTypeAttribute {

        const templateControlTypeAttribute: templateFormData.ITemplateControlTypeAttribute = {
            ID: controlTypeAttribute.ID(),
            MinLength: controlTypeAttribute.MinLength(),
            MaxLength: controlTypeAttribute.MaxLength(),
            MinVal: controlTypeAttribute.MinVal(),
            MaxVal: controlTypeAttribute.MaxVal(),
            Format: controlTypeAttribute.Format(),
            NumDecimals: controlTypeAttribute.NumDecimals(),
            NumericDefaultValue: controlTypeAttribute.NumericDefaultValue(),
            Interval: controlTypeAttribute.Interval(),
        };

        return templateControlTypeAttribute;
    }

    private getFields(fields: KnockoutObservableArray<IFieldObservable>): templateFormData.ITemplateField[] {
        const templateFormFields: templateFormData.ITemplateField[] = [];

        fields().forEach((field: IFieldObservable) => {
            const templateFormField: templateFormData.ITemplateField = {
                ID: field.ID(),
                FieldText: field.FieldText(),
                FieldValue: field.FieldValue(),
                SortOrder: field.SortOrder(),
                IsActive: field.IsActive(),
                HasException: field.HasException(),
                HasExceptionForm: field.HasExceptionForm(),
                TemplateFormID: field.TemplateFormID(),
            };
            templateFormFields.push(templateFormField);
        });

        return templateFormFields;
    }

    private getDuplicateFields(fields: KnockoutObservableArray<IFieldObservable>): templateFormData.ITemplateField[] {
        const templateFormFields: templateFormData.ITemplateField[] = [];

        fields().forEach((field: IFieldObservable) => {
            const templateFormField: templateFormData.ITemplateField = {
                ID: 0,
                FieldText: field.FieldText(),
                FieldValue: field.FieldValue(),
                SortOrder: field.SortOrder(),
                IsActive: field.IsActive(),
                HasException: field.HasException(),
                HasExceptionForm: field.HasExceptionForm(),
                TemplateFormID: field.TemplateFormID(),
            };
            templateFormFields.push(templateFormField);
        });

        return templateFormFields;
    }

    private getTemplateFormItems(): templateFormData.ITemplateFormItem[] {
        const me: TemplateFormController = this;
        const templateFormItems: templateFormData.ITemplateFormItem[] = [];

        this.viewModel.TemplateForm.FormItems().forEach((formItem: IFormItemObservable) => {

            const templateFormItem: templateFormData.ITemplateFormItem = {
                ID: formItem.ID(),
                FormText: formItem.FormText(),
                FormTextTranslations: me.createTranslationTextsFromObservables(formItem.FormTextTranslations),
                TemplateFormID: formItem.TemplateFormID(),
                TemplatePhraseID: ((formItem.PhraseOrCustomChoice() === Constants.TEMPLATE_FORM_CHOICES_PHRASES
                        || formItem.ControlTypeID() === Constants.QuestionType.TEXTBOX || formItem.ControlTypeID() === Constants.QuestionType.TEXTAREA
                        || formItem.ControlTypeID() === Constants.QuestionType.MULTIPLECOMMENTS
                        || formItem.ControlTypeID() === Constants.QuestionType.MULTIPLECOMMENTSPICS)
                        ? formItem.TemplatePhraseID() : 0),
                // For controls Dropdown,Radio When a single Additionalform is changed to multipleAdditional form by selection "Vary by selection",
                // we need to reset the AdditionalFormID to 0
                TemplateAdditionalFormID:  formItem.MultipleAdditionalForms() ? 0 : formItem.TemplateAdditionalFormID(),
                ControlTypeID: formItem.ControlTypeID(),
                IsRequired: formItem.IsRequired(),
                SortOrder: formItem.SortOrder(),
                IsReadOnly: formItem.IsReadOnly(),
                IsVisible: formItem.IsVisible(),
                IsActive: formItem.IsActive(),
                Fields: me.getFields(formItem.Fields),
                TemplateControlTypeAttribute: me.getControlTypeAttribute(formItem.TemplateControlTypeAttribute),
                TemplateFormItemDocumentTypeAllowed: me.getTemplateFormItemDocumentTypeAllowedFromObservable(formItem.TemplateFormItemDocumentTypeAllowed),
                LinkedCustomerDocuments: me.getTemplateFormItemLinkedCustomerDocumentsFromObservable(formItem.LinkedCustomerDocuments),
                IsEdit: false,
                MultipleAdditionalForms: formItem.MultipleAdditionalForms(),
                CopyAnswerWhenCloningTask: formItem.CopyAnswerWhenCloningTask(),
                ShowOnCalendar: formItem.ShowOnCalendar(),
                TargetTemplateID: formItem.TargetTemplateID(),
                PhraseOrCustomChoice: formItem.PhraseOrCustomChoice(),
                ExternalName: formItem.ExternalName(),
                ReportName: formItem.ReportName(),
            };
            templateFormItems.push(templateFormItem);
        });

        return templateFormItems;
    }

    private getTemplateFormItemLinkedCustomerDocumentsFromObservable(linkedDocs: KnockoutObservableArray<ITemplateFormItemLinkedCustomerDocumentObservable>)
        : templateFormData.ITemplateFormItemLinkedCustomerDocument[] {
        const result: templateFormData.ITemplateFormItemLinkedCustomerDocument[] = [];

        linkedDocs().forEach((item) => {
            result.push({
                CustomerDocumentID: item.CustomerDocumentID(),
                Description: item.Description(),
                FileName: item.FileName(),
                IsActive: item.IsActive(),
                TemplateFormItemCustomerDocumentID: item.TemplateFormItemCustomerDocumentID(),
            });
        });

        return result;
    }

    // used to update the URL when a new record is added
    private updateURL(newID: number, FormName: string): void {
        const app: IApplication = Global.app();
        const routeChange: EventUtil.IRouteChange = {
            appRouterID: this.appRouterID([]),
            url: "/TemplateForm/" + newID,
            title: this.title(),
            subTitle: FormName,
            newAppRouterID: this.appRouterID([newID]),
        };

        app.events().trigger(EventUtil.EVENT_ROUTECHANGE, routeChange);
    }

    private GetCodeModelObservable(src: Data.ICodeModel<number>): any {
        return {
            ID: ko.observable(src ? src.ID : 0),
            Name: ko.observable(src ? src.Name : ""),
        };
    }

    private getFormatMasksForControlType(controlTypeID: number): templateFormData.IFormatMask[] {
        const formatMasks: templateFormData.IFormatMask[] = [];

        const controlTypeMatches = this.viewModel.ControlTypes().filter((x) => x.ID === controlTypeID);
        if (controlTypeMatches.length > 0 &&
            controlTypeMatches[0].Formats.length > 0) {
            formatMasks.push({
                ID: 0,
                UniqueID: "",
                Description: Constants.PHRASE_NOTSELECTED,
                WebMask: "",
            });
            for (const format of controlTypeMatches[0].Formats) {
                formatMasks.push({
                    ID: format.ID,
                    UniqueID: format.UniqueID,
                    Description: format.Description,
                    WebMask: format.WebMask,
                });
            }
        }
        return formatMasks;
    }

    private createEmptyFormTextTranslations(): ITranslatedTextObservable[] {
        const userSession = Global.app().userSession;
        if (!userSession || !userSession.Languages) {
            return [];
        }

        return userSession.Languages.map((language) => {
            const translatedTextObservable: ITranslatedTextObservable = {
                LanguageID: language.ID,
                LanguageName: language.Name,
                TranslatedText: ko.observable(""),
            };
            return translatedTextObservable;
        });
    }

    private createTranslationTextsFromObservables(translatedTextObservables: KnockoutObservableArray<ITranslatedTextObservable>): ITranslatedText[] {
        return translatedTextObservables().map((translatedTextObservable) => {
            const translatedText: ITranslatedText = {
                LanguageID: translatedTextObservable.LanguageID,
                TranslatedText: translatedTextObservable.TranslatedText(),
            };
            return translatedText;
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/templateForm.ts